import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEO = ({ titleKey, descriptionKey }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const baseUrl = 'https://bhoomeeawards.com'; // Update with your actual domain
  const languages = [
    { code: 'en', region: 'IN' },
    { code: 'kn', region: 'IN' },
  ];

  // Get current path without language prefix
  // Example: /kn/about -> /about
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentLang = languages.find(lang => lang.code === pathParts[0]);
  const pathWithoutLang = currentLang
    ? '/' + pathParts.slice(1).join('/')
    : location.pathname;

  // Generate URLs for all language versions
  const generateUrl = (lang) => {
    const cleanPath = pathWithoutLang === '/' ? '' : pathWithoutLang;
    return `${baseUrl}/${lang}${cleanPath}`;
  };

  // Get the translated title and description
  const title = titleKey ? t(titleKey) : 'Bhoomee Awards';
  const description = descriptionKey ? t(descriptionKey) : 'Celebrating agricultural excellence in Karnataka';

  return (
    <Helmet>
      {/* Primary meta tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <html lang={i18n.language} />

      {/* Canonical URL - current language version */}
      <link rel="canonical" href={generateUrl(i18n.language)} />

      {/* Hreflang tags for all languages */}
      {languages.map(({ code, region }) => (
        <link
          key={code}
          rel="alternate"
          hreflang={`${code}-${region}`}
          href={generateUrl(code)}
        />
      ))}

      {/* Default fallback (usually English) */}
      <link rel="alternate" hreflang="x-default" href={generateUrl('en')} />

      {/* Open Graph for social media */}
      <meta property="og:locale" content={`${i18n.language}_IN`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={generateUrl(i18n.language)} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Bhoomee Awards" />

      {/* Alternate locales for social media */}
      {languages
        .filter(lang => lang.code !== i18n.language)
        .map(({ code }) => (
          <meta
            key={code}
            property="og:locale:alternate"
            content={`${code}_IN`}
          />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

export default SEO;
